import React, { useEffect, useRef, useState } from "react";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { axiosInstance } from "contexts/axiosApiInstance";
import { UserAccountResponse } from "components/entities";
import {
  AvatarInitial,
  invalidatePhotoCache,
} from "components/common/AvatarInitial";
import { EditableName } from "components/canvas/utils/EditableName";
import { DropdownMenu } from "components/common/DropdownMenu";
import { MenuItem } from "components/common/MenuItem";
import { createLogger } from "debug/DebugingLogger";
import { showNotificationGlobal } from "stores/useNotificationStore";
import {
  IMAGE_ACCEPTED_TYPES,
  IMAGE_ACCEPT_STRING,
  IMAGE_MAX_FILE_SIZE,
  resizeImage,
} from "@/lib/images";

const log = createLogger("SettingsDialog");

interface ProfileHeaderProps {
  accountDetails: UserAccountResponse | undefined;
  email: string;
  onAccountUpdated: () => void;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  accountDetails,
  email,
  onAccountUpdated,
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [showSavedTick, setShowSavedTick] = useState(false);
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const savedTickTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const avatarRef = useRef<HTMLButtonElement>(null);

  const savedName = accountDetails?.account_details?.name || "";
  const currentPhoto = accountDetails?.account_details?.photo || "";

  useEffect(
    () => () => {
      if (savedTickTimer.current) clearTimeout(savedTickTimer.current);
    },
    [],
  );

  const handleUploadPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (
      !IMAGE_ACCEPTED_TYPES.includes(
        file.type as (typeof IMAGE_ACCEPTED_TYPES)[number],
      )
    ) {
      showNotificationGlobal(
        "Please upload a JPEG, PNG, WebP, or SVG image",
        "error",
      );
      return;
    }

    if (file.size > IMAGE_MAX_FILE_SIZE) {
      showNotificationGlobal("Image must be under 1 MB", "error");
      return;
    }

    setIsUploadingPhoto(true);
    try {
      const dataUrl = await resizeImage(file);
      await axiosInstance.put("/action/user/photo", { photo: dataUrl });
      invalidatePhotoCache();
      onAccountUpdated();
    } catch (error) {
      log.error("Failed to upload photo: %o", error);
      const message =
        error instanceof Error && error.message === "Failed to load image"
          ? "Could not process image — the file may be corrupted"
          : "Failed to upload photo";
      showNotificationGlobal(message, "error");
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    setIsUploadingPhoto(true);
    try {
      await axiosInstance.delete("/action/user/photo");
      invalidatePhotoCache();
      onAccountUpdated();
    } catch (error) {
      log.error("Failed to remove photo: %o", error);
      showNotificationGlobal("Failed to remove photo", "error");
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // `EditableName.commit` is called synchronously and inspects the return value,
  // so this must stay sync — fire the PUT off and handle its result later.
  const commitName = (name: string): void => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === savedName) return;
    void axiosInstance
      .put("/action/user/display-name", { name: trimmed })
      .then(() => {
        setShowSavedTick(true);
        if (savedTickTimer.current) clearTimeout(savedTickTimer.current);
        savedTickTimer.current = setTimeout(
          () => setShowSavedTick(false),
          2000,
        );
        onAccountUpdated();
      })
      .catch((error: unknown) => {
        log.error("Failed to update display name: %o", error);
        showNotificationGlobal("Failed to update display name", "error");
      });
  };

  // No photo yet: there is nothing to remove, so the click goes straight to
  // the file picker instead of a one-item menu.
  const onAvatarClick = () => {
    if (isUploadingPhoto) return;
    if (currentPhoto) setShowPhotoMenu((v) => !v);
    else fileInputRef.current?.click();
  };

  return (
    <div className="settings-profile">
      <button
        ref={avatarRef}
        type="button"
        className="settings-avatar"
        aria-label={currentPhoto ? "Change photo" : "Upload photo"}
        aria-haspopup={currentPhoto ? "menu" : undefined}
        aria-expanded={currentPhoto ? showPhotoMenu : undefined}
        data-busy={isUploadingPhoto || undefined}
        onClick={onAvatarClick}
      >
        <AvatarInitial
          name={savedName}
          icon={currentPhoto}
          color={accountDetails?.account_details?.color || ""}
          clientId={accountDetails?.account_details?.id}
          size="md"
        />
        <span className="settings-avatar-overlay" aria-hidden>
          {isUploadingPhoto ? (
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <CameraAltOutlinedIcon sx={{ fontSize: 16 }} />
          )}
        </span>
      </button>
      <DropdownMenu
        anchorRef={avatarRef}
        isOpen={showPhotoMenu}
        onClose={() => setShowPhotoMenu(false)}
        style={{ width: "auto", minWidth: "9rem" }}
      >
        <MenuItem
          Icon={CameraAltOutlinedIcon}
          label="Upload photo"
          onClick={() => {
            setShowPhotoMenu(false);
            fileInputRef.current?.click();
          }}
        />
        <MenuItem
          Icon={DeleteOutlineOutlinedIcon}
          label="Remove photo"
          onClick={() => {
            setShowPhotoMenu(false);
            void handleRemovePhoto();
          }}
        />
      </DropdownMenu>
      <input
        ref={fileInputRef}
        type="file"
        accept={IMAGE_ACCEPT_STRING}
        className="hidden"
        onChange={handleUploadPhoto}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="settings-name">
          <EditableName
            name={savedName || "Not set"}
            isEditing={isEditingName}
            canEdit={true}
            editTrigger="click"
            setIsEditing={setIsEditingName}
            update={() => {}}
            commit={commitName}
            abort={() => setIsEditingName(false)}
            editColor="var(--color-accent)"
            ariaLabel="Display name"
            maxLength={100}
          />
          {!isEditingName && showSavedTick && (
            <CheckIcon
              sx={{ fontSize: 14, color: "var(--color-success)" }}
              aria-label="Saved"
            />
          )}
        </div>
        <span className="settings-email">{email}</span>
      </div>
    </div>
  );
};
